import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { loginSuccess, logout, setAuthLoading, restoreSession } from './store/authSlice';
import { setUser, fetchUserPermissionsThunk, clearUser } from './store/userSlice';
import { fetchEmailSignature } from './store/emailSignatureSlice';

// ✅ UPDATED: Import listeners for both case and direct messages
import {
  listenForGlobalUnreadMessages,
  stopListeningForGlobalUnreadMessages,
  listenForDirectThreads,
  stopListeningForDirectThreads,
  fetchUsersForMessaging
} from './store/messagingSlice';

import { authService } from './services/authService';
import { apiClient } from './services/apiClient';
import { loadTenantContext, clearTenantContext } from './services/tenantContextService';
import { useDesktopMessageNotifications } from './hooks/useDesktopMessageNotifications';
import { toast } from './hooks/use-toast';
import { lazyRetry } from './utils/lazyRetry';

// Styles
import './main.css';

// Components
import Login from './components/Auth/Login';
import Loader from './components/UI/Loader';
import SessionTimeout from './components/Auth/SessionTimeout';
import { Toaster } from "./components/UI/toaster";
import { ViewModeProvider } from './hooks/useViewMode';

// PatientVerificationModal is now lazy loaded
const PatientVerificationModal = React.lazy(() => lazyRetry(() => import('./components/Profile/Patient/PatientVerificationModal')));

const AppRoutes = React.lazy(() => lazyRetry(() => import('./routes')));

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, loading } = useSelector((state) => state.auth);
  const { userData } = useSelector((state) => state.user);
  const sessionTimeoutEnabled = String(process.env.REACT_APP_ENABLE_SESSION_TIMEOUT || '').toLowerCase() === 'true';
  useDesktopMessageNotifications();

  useEffect(() => {
    // Set the logout callback for apiClient
    apiClient.setLogoutCallback(() => dispatch(logout()));

    // Initialize token refresh listener
    authService.setupTokenRefreshListener();

    const handleAuthChange = async (event) => {
      const { authenticated, user } = event.detail;

      if (authenticated && user) {
        // Load the tenant context before anything else. It carries the
        // Firestore database this tenant's data lives in, and every live
        // listener opened before it lands would attach to (default).
        try {
          await loadTenantContext();
        } catch (err) {
          console.error('[tenant] context unavailable; flags fall back to build defaults', err);
        }
        dispatch(loginSuccess({ user }));
        dispatch(setUser({ user }));
        dispatch(fetchUserPermissionsThunk());
      } else {
        clearTenantContext();
        dispatch(clearUser());
        // ✅ UPDATED: Clean up both case and direct message listeners on logout
        dispatch(stopListeningForGlobalUnreadMessages());
        dispatch(stopListeningForDirectThreads());
      }
    };

    const handleTokenRefreshWarning = (event) => {
      const message = event?.detail?.message || 'Session refresh is having trouble. Some actions may fail until connection is restored.';
      toast({
        title: 'Session Refresh Warning',
        description: message,
        variant: 'destructive',
      });
    };

    window.addEventListener('authStatusChanged', handleAuthChange);
    window.addEventListener('authTokenRefreshWarning', handleTokenRefreshWarning);

    const initializeAuth = async () => {
      try {
        dispatch(setAuthLoading(true));
        const result = await authService.checkAuthStatus();

        if (result?.user) {
          // Must precede the Firestore listeners started below — they attach to
          // whichever database the client is currently pointed at.
          try {
            await loadTenantContext();
          } catch (err) {
            console.error('[tenant] context unavailable; flags fall back to build defaults', err);
          }

          dispatch(restoreSession({ user: result.user }));
          dispatch(setUser({ user: result.user }));
          dispatch(fetchUserPermissionsThunk());

          // ✅ UPDATED: Fetch users first for sender name resolution, then start message listeners
          dispatch(fetchUsersForMessaging());
          dispatch(fetchEmailSignature());
          dispatch(listenForGlobalUnreadMessages({
            currentUserId: result.user.id
          }));
          dispatch(listenForDirectThreads({
            userId: result.user.id
          }));
        } else {
          dispatch(setAuthLoading(false));
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        dispatch(setAuthLoading(false));
      }
    };

    initializeAuth();

    return () => {
      window.removeEventListener('authStatusChanged', handleAuthChange);
      window.removeEventListener('authTokenRefreshWarning', handleTokenRefreshWarning);
    };
  }, [dispatch]);

  // Show loader while checking auth
  if (loading) {
    return <Loader />;
  }

  return (
    <div className="app">
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/" replace /> : <Login />
            }
          />
          <Route
            path="/*"
            element={
              isAuthenticated && userData ? (
                <div className="mx-auto w-full max-w-[1800px]">
                  {sessionTimeoutEnabled && <SessionTimeout />}
                  <ViewModeProvider>
                    <AppRoutes userRole={userData.role} />
                  </ViewModeProvider>
                </div>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="/verify-patient/*" element={<PatientVerificationModal />} />
        </Routes>
      </Suspense>
      <Toaster />
    </div>
  );
}

export default App;
